import React, { useState, useEffect, useRef } from "react";
import { TabContent, TabPane } from "reactstrap";
import {
  Home,
  Palette,
  Settings,
  UserCheck,
  Users,
  Calendar,
  MessageSquare,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Target,
  ArrowRight,
  CheckCircle,
  MessageCircle,
  Menu,
  X,
  FileText,
} from "lucide-react";
import "./WelcomeStyles.css";
import { Sidebar } from "../Layout/Sidebar/Sidebar";
import EventSetup from "../EventBranding/EventSetup";
import EventBranding from "../EventBranding/EventBranding";
import Participants from "../Participants/Participants";
import Registration from "../Registrattion/Registration";
import Sessions from "../Sessions/Sessions";
import Exhibition from "../Exhibition/Exhibition";
import Marketing from "../Marketing/Marketing";
import Analytics from "../Analytics/AnalyticsDashboard";
import EventLandingBuilder from "../EventLandingBuilder/EventLandingBuilderImproved";
import Survey from "../Survey/Survey";

const getTabIndex = (id) => {
  const map = {
    "landing-builder": "9",
    "event-setup": "1",
    "event-branding": "2",
    registration: "3",
    participants: "4",
    sessions: "5",
    survey: "10",
    marketing: "7",
    analytics: "8",
    guide: "11",
  };
  return map[id];
};

const WelcomeHero = ({ onNavigateToTab }) => {
  const [expandedSection, setExpandedSection] = useState("getting-started");

  const menuItems = [
    {
      id: "landing-builder",
      icon: Palette,
      title: "Event Landing Page Builder",
      description:
        "Create stunning, professional landing pages with integrated branding and customization tools.",
      features: [
        "3 Professional Templates",
        "Integrated Branding",
        "Logo & Banner Upload",
        "Mobile Responsive",
        "Real-time Preview",
      ],
      color: "#f97316",
      status: "Popular",
    },
    {
      id: "registration",
      icon: UserCheck,
      title: "Registration & Ticketing",
      description:
        "Manage event registrations, create tickets, and handle attendee sign-ups.",
      features: ["Ticket Creation", "Pricing Tiers", "Registration Forms", "Payment Processing"],
      color: "#10b981",
      status: "Revenue",
    },
    {
      id: "participants",
      icon: Users,
      title: "Participants",
      description: "View and manage all event attendees, speakers, and participants.",
      features: ["Attendee List", "Speaker Management", "Participant Roles", "Communication"],
      color: "#f59e0b",
      status: "Management",
    },
    {
      id: "sessions",
      icon: Calendar,
      title: "Sessions",
      description: "Create and manage event sessions, schedules, and speaker assignments.",
      features: ["Session Scheduling", "Speaker Assignment", "Time Management", "Session Types"],
      color: "#ef4444",
      status: "Scheduling",
    },
    {
      id: "survey",
      icon: FileText,
      title: "Survey",
      description: "Create and manage surveys to collect feedback from your event attendees.",
      features: ["Custom Questions", "QR Code Generation", "Real-time Analytics", "Response Tracking"],
      color: "#8b5cf6",
      status: "Feedback",
    },
    {
      id: "marketing",
      icon: MessageSquare,
      title: "Marketing",
      description: "Send emails, SMS, and push notifications to promote your event.",
      features: ["Email Campaigns", "SMS Marketing", "Push Notifications", "WhatsApp Integration"],
      color: "#06b6d4",
      status: "Promotion",
    },
    {
      id: "analytics",
      icon: BarChart3,
      title: "Analytics",
      description: "Track registrations, engagement, and event performance with detailed insights.",
      features: ["Registration Analytics", "Engagement Metrics", "Revenue Tracking", "Attendee Insights"],
      color: "#84cc16",
      status: "Insights",
    },
  ];

  const quickStartSteps = [
    { step: 1, title: "Create Landing Page", description: "Start by build a professional landing page with branding and templates", icon: Palette, color: "#f97316" },
    { step: 2, title: "Set Up Registration", description: "Configure ticket types and registration forms", icon: UserCheck, color: "#10b981" },
    { step: 3, title: "Invite Attendees", description: "Manage participants and send invitations", icon: Users, color: "#3b82f6" },
    { step: 4, title: "Launch & Promote", description: "Use marketing tools to promote your event", icon: MessageSquare, color: "#06b6d4" },
  ];

  const toggleSection = (sectionId) => setExpandedSection(expandedSection === sectionId ? null : sectionId);

  const getStatusColor = (status) => {
    const colors = {
      Popular: "#f97316",
      Essential: "#3b82f6",
      New: "#10b981",
      Revenue: "#059669",
      Management: "#f59e0b",
      Scheduling: "#ef4444",
      Feedback: "#8b5cf6",
      Promotion: "#06b6d4",
      Insights: "#84cc16",
    };
    return colors[status] || "#6b7280";
  };

  return (
    <div className="welcome-container">
      <div className="welcome-hero">
        <div className="welcome-hero-content">
          <div className="welcome-hero-badge">
            <Sparkles size={16} /> Welcome to Kago Events
          </div>
          <h1>Your Complete Event Management Platform</h1>
          <p>Create, manage, and promote successful events with our comprehensive suite of tools.</p>
          <div className="welcome-hero-stats">
            <div className="welcome-stat"><div className="welcome-stat-number">Web</div><div className="welcome-stat-label">Application</div></div>
            <div className="welcome-stat"><div className="welcome-stat-number">Mobile</div><div className="welcome-stat-label">App Available</div></div>
            <div className="welcome-stat"><div className="welcome-stat-number">99.9%</div><div className="welcome-stat-label">Uptime</div></div>
          </div>
        </div>
        <div className="welcome-hero-visual">
          <div className="welcome-hero-card">
            <div className="welcome-hero-card-header">
              <div className="welcome-hero-card-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <div className="welcome-hero-card-content">
              <div className="welcome-hero-feature"><CheckCircle size={16} /><span>Professional Templates</span></div>
              <div className="welcome-hero-feature"><CheckCircle size={16} /><span>Real-time Analytics</span></div>
              <div className="welcome-hero-feature"><CheckCircle size={16} /><span>Integrated Marketing</span></div>
              <div className="welcome-hero-feature"><CheckCircle size={16} /><span>Secure Payments</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="welcome-section">
        <div
          className="welcome-section-header"
          onClick={() => toggleSection("getting-started")}
        >
          <div className="welcome-section-title">
            <Target className="welcome-section-icon" />
            <h2>Quick Start Guide</h2>
            <span className="welcome-section-badge">4 Steps</span>
          </div>
          {expandedSection === "getting-started" ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>
        {expandedSection === "getting-started" && (
          <div className="welcome-section-content">
            <div className="welcome-steps">
              {quickStartSteps.map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.step} className="welcome-step">
                    <div className="welcome-step-number" style={{ backgroundColor: step.color }}>
                      {step.step}
                    </div>
                    <div className="welcome-step-content">
                      <div className="welcome-step-header">
                        <IconComponent size={20} style={{ color: step.color }} />
                        <h3>{step.title}</h3>
                      </div>
                      <p>{step.description}</p>
                    </div>
                    {index < quickStartSteps.length - 1 && (
                      <div className="welcome-step-connector"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="welcome-section">
        <div
          className="welcome-section-header"
          onClick={() => toggleSection("dashboard-overview")}
        >
          <div className="welcome-section-title">
            <Home className="welcome-section-icon" />
            <h2>Key Features</h2>
            <span className="welcome-section-badge">7 Tools</span>
          </div>
          {expandedSection === "dashboard-overview" ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </div>
        {expandedSection === "dashboard-overview" && (
          <div className="welcome-section-content">
            <div className="welcome-tools-grid">
              {menuItems.map((item) => {
                const IconComponent = item.icon;
                const tabIndex = getTabIndex(item.id);
                return (
                  <div key={item.id} className="welcome-tool-card">
                    <div className="welcome-tool-header">
                      <div
                        className="welcome-tool-icon"
                        style={{
                          backgroundColor: `${item.color}15`,
                          color: item.color,
                        }}
                      >
                        <IconComponent size={24} />
                      </div>
                      <div
                        className="welcome-tool-status"
                        style={{
                          backgroundColor: getStatusColor(item.status),
                        }}
                      >
                        {item.status}
                      </div>
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="welcome-tool-features">
                      {item.features.map((feature, idx) => (
                        <span key={idx} className="welcome-tool-feature">
                          <CheckCircle size={12} /> {feature}
                        </span>
                      ))}
                    </div>
                    <button
                      className="welcome-tool-button welcome-explore-button"
                      style={{
                        borderColor: item.color,
                        color: item.color,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (typeof onNavigateToTab === "function" && tabIndex) {
                          onNavigateToTab(tabIndex);
                        }
                      }}
                    >
                      <span>Explore</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="welcome-support">
        <div className="welcome-support-content">
          <div className="welcome-support-text">
            <h2>Need Help Getting Started?</h2>
            <p>Our support team is here to help you create amazing events.</p>
          </div>
          <div className="welcome-support-actions">
            <button
              className="welcome-support-button secondary"
              onClick={() => {
                if (typeof onNavigateToTab === "function") {
                  onNavigateToTab("11");
                }
                window.open(
                  "https://tawk.to/chat/65fd674a1ec1082f04da1ddc/1hpitmvma",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              <MessageCircle size={16} /> Chat with Expert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Welcome = ({ getEventDetails, token, userID, isLoading }) => {
  const [selectActiveTab, setSelectActiveTab] = useState("0");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  
  // ⏱️ SESSION TIMEOUT STATE
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  
  const navigate = (path) => {
    window.location.href = path;
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // ===== SESSION TIMEOUT LOGIC =====
  useEffect(() => {
    console.log('⏱️ Welcome: Session timeout check - token prop exists:', !!token);
    
    // ✅ Use token PROP instead of localStorage
    if (!token) {
      console.warn('⚠️ Welcome: Session timeout: No token prop - skipping');
      return;
    }

    // ⏱️ PRODUCTION CONFIG: 15 minutes total (13m idle + 2m warning)
    const IDLE_TIMEOUT = 10 * 60 * 1000;  // 15 minutes (900000ms)
    const WARNING_TIME = 2 * 60 * 1000;    // 2 minute warning (120000ms)
    
    console.log(`✅ Welcome: Session timeout started - Idle: ${IDLE_TIMEOUT/1000/60}min, Warning: ${WARNING_TIME/1000/60}min`);

    const resetIdleTimer = () => {
      // ⚠️ IMPORTANT: Only reset if warning is NOT showing
      // Once warning appears, mouse movement should NOT dismiss it
      if (showSessionWarning) {
        return; // Don't reset timers while warning is active
      }

      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);

      // Start warning timer (show warning after idle period)
      warningTimerRef.current = setTimeout(() => {
        console.log('⚠️ Welcome: Session expiring in 2 minutes!');
        setShowSessionWarning(true);

        // Force logout after warning period (user must click a button)
        idleTimerRef.current = setTimeout(() => {
          console.log('🚪 Welcome: Auto-logging out after warning period...');
          performLogout();
        }, WARNING_TIME);
      }, IDLE_TIMEOUT - WARNING_TIME);
    };

    const performLogout = () => {
      console.log('🚪 Welcome: Logging out user...');
      // Clear localStorage just in case
      const keys = ['token', 'authToken', 'accessToken', 'user', 'authEventKagoUser'];
      keys.forEach(key => localStorage.removeItem(key));
      
      navigate('/login');
    };

    // Listen for user activity (only resets timer if warning is not showing)
    const events = ['mousedown', 'keydown', 'scroll', 'mousemove', 'click', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));

    // Start initial timer
    resetIdleTimer();

    // Cleanup
    return () => {
      console.log('🧹 Welcome: Session timeout: Cleaning up listeners and timers');
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [token, showSessionWarning]); // ✅ Add showSessionWarning as dependency
  // ===== END SESSION TIMEOUT =====

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && !sidebarOpen) {
        setSidebarOpen(true);
      } else if (window.innerWidth <= 768 && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      if (sidebarOpen) {
        document.body.classList.add('sidebar-open');
        document.body.classList.remove('sidebar-closed');
      } else {
        document.body.classList.add('sidebar-closed');
        document.body.classList.remove('sidebar-open');
      }
    } else {
      document.body.classList.remove('sidebar-open', 'sidebar-closed');
    }
  }, [sidebarOpen]);

  // ===== SESSION TIMEOUT WARNING MODAL HANDLERS =====
  const handleStayLoggedIn = () => {
    console.log('🖱️ Welcome: User clicked "Stay Logged In"');
    setShowSessionWarning(false);
    // Timer will auto-reset via the useEffect dependency on showSessionWarning
  };

  const handleLogoutNow = () => {
    console.log('🚪 Welcome: User clicked "Log Out Now"');
    setShowSessionWarning(false);
    const keys = ['token', 'authToken', 'accessToken', 'user', 'authEventKagoUser'];
    keys.forEach(key => localStorage.removeItem(key));
    navigate('/login');
  };

  return (
    <div className="welcome-layout-container">
      
      {/* ⏱️ SESSION TIMEOUT WARNING MODAL - STAYS UNTIL USER CLICKS */}
      {showSessionWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', padding: '40px 32px', borderRadius: '16px',
            maxWidth: '450px', width: '90%', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px', height: '80px', background: '#fff7ed',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 24px'
            }}>
              <span style={{ fontSize: '40px' }}>⏰</span>
            </div>
            
            <h2 style={{ margin: '0 0 12px', color: '#1e293b', fontSize: '24px', fontWeight: '700' }}>
              Session Expiring Soon
            </h2>
            
            <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '16px', lineHeight: 1.5 }}>
              For your security, you'll be logged out in <strong style={{ color: '#f97316' }}>2 minutes</strong> due to inactivity.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* ✅ TWO BUTTONS AS REQUESTED */}
              <button
                onClick={handleLogoutNow}
                style={{
                  padding: '12px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#475569',
                  fontWeight: '600', fontSize: '14px', transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                Log Out Now
              </button>
              
              <button
                onClick={handleStayLoggedIn}
                style={{
                  padding: '12px 24px', background: 'linear-gradient(135deg, #FF7A00 0%, #ff8560f0 100%)',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff',
                  fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 14px rgba(255, 122, 0, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 122, 0, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 122, 0, 0.4)';
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
          
          <style>{`
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}

      <Sidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        selectActiveTab={selectActiveTab}
        setSelectActiveTab={setSelectActiveTab}
        getEventDetails={getEventDetails}
      />

      <div className="welcome-main-content">
        {/* ✅ CRITICAL: mt-0 removes Bootstrap's default top margin */}
        <TabContent activeTab={selectActiveTab} className="text-muted mt-0">
          <TabPane tabId="0" className="welcome-tab-zero">
            <WelcomeHero onNavigateToTab={(tab) => setSelectActiveTab(tab)} />
          </TabPane>

          <TabPane tabId="1">
            <div className="p-1">
              <EventSetup eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
            </div>
          </TabPane>

          <TabPane tabId="2">
            <div className="px-5 py-5">
              <EventBranding eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
            </div>
          </TabPane>

          <TabPane tabId="3">
            <Registration eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
          </TabPane>

          <TabPane tabId="4">
            <Participants eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
          </TabPane>

          <TabPane tabId="5">
            <div className="px-5 py-5">
              <Sessions eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
            </div>
          </TabPane>

          <TabPane tabId="10">
            <div className="p-1">
              <Survey
                eventDetails={getEventDetails?.data}
                token={token}
                userID={userID}
                isLoading={isLoading}
              />
            </div>
          </TabPane>

          <TabPane tabId="7">
            <Marketing eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
          </TabPane>

          <TabPane tabId="8">
            <div className="p-1">
              <Analytics eventDetails={getEventDetails?.data} token={token} userID={userID} isLoading={isLoading} />
            </div>
          </TabPane>

          <TabPane tabId="9">
            <div className="p-0">
              <EventLandingBuilder eventDetails={getEventDetails?.data} token={token} userID={userID} />
            </div>
          </TabPane>

          <TabPane tabId="11">
            <div className="p-1">
              <h2>Guide & Documentation</h2>
              <p>Coming soon - comprehensive guides and tutorials.</p>
            </div>
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
};

export default Welcome;